import { FC, ReactNode } from "react";

import { Stack, SxProps, Theme } from "@mui/material";

import { mergeSx } from "@/common/helpers/mergeSx";
import { FormBlock } from "@/forms-design-system/FormBlock";
import { FormButton } from "@/forms-design-system/FormButton";
import { FormDivider } from "@/forms-design-system/FormDivider";
import { FormTypography } from "@/forms-design-system/FormTypography";

interface FormListBlockProps {
  title: string;
  children: ReactNode;
  onViewAllClick?: () => void;
  viewAllLabel?: string;
  showViewAllButton?: boolean;
  id?: string;
  "data-test"?: string;
  sx?: SxProps<Theme>;
}

export const FormListBlock: FC<FormListBlockProps> = ({
  title,
  children,
  onViewAllClick,
  viewAllLabel,
  showViewAllButton = false,
  id,
  "data-test": dataTest,
  sx,
}) => {
  return (
    <FormBlock
      fullWidth
      sx={mergeSx(
        (theme) => ({
          padding: { xs: theme.spacing(3, 2), md: 3 },
          border: `1px solid ${theme.palette.forms.border.moderate}`,
          borderRadius: 1,
          backgroundColor:
            theme.palette.metadata?.mode === "dark"
              ? theme.palette.forms.surface.intense
              : theme.palette.forms.surface.supershy,
        }),
        sx
      )}
      id={id}
      data-test={dataTest}
    >
      <Stack alignItems="flex-start" gap={2} width="100%">
        <FormTypography variant="subtitle1" vibe="intense">
          {title}
        </FormTypography>
        <Stack width="100%" gap={1.5} divider={<FormDivider />}>
          {children}
        </Stack>
        {showViewAllButton && viewAllLabel && onViewAllClick && (
          <FormButton variant="outline" size="small" onClick={onViewAllClick}>
            {viewAllLabel}
          </FormButton>
        )}
      </Stack>
    </FormBlock>
  );
};
